'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { JwtPayload } from '@workspace/ui/types';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'sonner';
import { updateCurrentOrganisation } from '@/actions/organisation';
import { createJwtUserFromPayload } from '@/lib/active-organisation';
import {
  broadcastOrganisationSwitch,
  reloadAfterOrganisationSwitch,
} from '@/lib/organisation-switch-channel';
import type { OrganisationSwitcherItem } from '@/lib/organisation-switcher-items';
import { useAuthStore } from '@/stores/authStore';

interface UseSwitchOrganisationItemOptions {
  onSwitched?: () => void;
}

interface UseSwitchOrganisationItemResult {
  switchToItem: (item: OrganisationSwitcherItem) => void;
  isSwitching: boolean;
}

export function useSwitchOrganisationItem({
  onSwitched,
}: UseSwitchOrganisationItemOptions = {}): UseSwitchOrganisationItemResult {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const setAccessToken = useAuthStore((state) => state.setAccessToken);
  const setAuthenticated = useAuthStore((state) => state.setAuthenticated);
  const setUser = useAuthStore((state) => state.setUser);
  const setIsLoading = useAuthStore((state) => state.setIsLoading);
  const setOrganisationSwitching = useAuthStore((state) => state.setOrganisationSwitching);

  const userId = user?.user.id;

  const switchMutation = useMutation({
    mutationFn: (item: OrganisationSwitcherItem) => {
      if (!userId) return Promise.reject(new Error('Missing user id'));

      return updateCurrentOrganisation({
        user_id: userId,
        organisation_id: item.organisationId,
        ...(item.roleContext ? { role_context: item.roleContext } : {}),
      });
    },
    onMutate: () => {
      setOrganisationSwitching(true);
      setIsLoading(true);
    },
    onSuccess: (response, item) => {
      const accessToken = response.data?.auth.access_token;
      if (!accessToken) {
        setOrganisationSwitching(false);
        setIsLoading(false);
        toast.error('Organisation switched, but no access token was returned.');
        return;
      }

      const decodedToken = jwtDecode<JwtPayload>(accessToken);
      setAccessToken(accessToken);
      setAuthenticated(true);
      setUser(createJwtUserFromPayload(decodedToken));
      queryClient.clear();
      broadcastOrganisationSwitch(item.organisationId);
      onSwitched?.();
      reloadAfterOrganisationSwitch(item.organisationId);
    },
    onError: (error) => {
      setOrganisationSwitching(false);
      setIsLoading(false);
      toast.error(error instanceof Error ? error.message : 'Failed to switch organisation.');
    },
  });

  return { switchToItem: switchMutation.mutate, isSwitching: switchMutation.isPending };
}
